import { Reveal, SectionLabel } from "@/components/Reveal";
import { leadership, team, avatarColors } from "@/data/team";
import { Seo } from "@/components/Seo";

const Avatar = ({ person, index, size = "large" }) => {
  const dims = size === "large" ? "h-full w-full" : "h-full w-full";
  if (person.photo) {
    return (
      <img
        src={person.photo}
        alt={person.name}
        className={`${dims} object-cover object-top`}
        loading="lazy"
      />
    );
  }
  const color = avatarColors[index % avatarColors.length];
  return (
    <div
      className="h-full w-full flex items-center justify-center"
      style={{ backgroundColor: `${color}18` }}
    >
      <span className="text-3xl md:text-4xl font-bold font-heading" style={{ color }}>
        {person.initials}
      </span>
    </div>
  );
};

export default function Team() {
  return (
    <div data-testid="team-page">
      <Seo
        title="Our Team"
        description="Meet the leadership and specialists behind Franchise India Knowledge Services — experienced consultants, brand specialists and operators dedicated to scaling knowledge businesses."
        path="/team"
      />
      {/* HEADER */}
      <section className="bg-navy noise-overlay text-white pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel className="text-gold">Our Team</SectionLabel>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl text-balance">
              The people behind the vision.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              A team of experienced consultants, brand specialists and operators
              dedicated to your success.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-20 md:py-28 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Leadership</SectionLabel>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadership.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.1}>
                <div
                  data-testid={`leader-card-${i}`}
                  className="bg-white border border-navy/10 flex flex-col sm:flex-row overflow-hidden h-full"
                >
                  <div className="w-full sm:w-56 h-64 sm:h-auto shrink-0 bg-canvas-alt">
                    <Avatar person={l} index={i} />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <h3 className="text-2xl font-semibold text-navy">{l.name}</h3>
                    <p className="text-teal text-sm uppercase tracking-[0.15em] font-semibold mt-1">
                      {l.role}
                    </p>
                    <p className="mt-4 text-ink-secondary leading-relaxed">{l.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM GRID */}
      <section className="pb-24 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>The Team</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy">
              Specialists across every function.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`team-card-${i}`}
                  className="group bg-white border border-navy/10 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-[4/5] bg-canvas-alt overflow-hidden">
                    <div className="h-full w-full group-hover:scale-105 transition-transform duration-500">
                      <Avatar person={m} index={i} size="small" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-navy leading-tight">{m.name}</h3>
                    <p className="mt-1 text-sm text-ink-muted">{m.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
